import { useMutation } from '@tanstack/react-query';
import { apiUserAccount } from '../../../utils/ApiService';
import { notification } from 'components/ds';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import { handleApiError } from 'components/ErrorHandler';
import { ValidatorField } from 'components/ValidatorField';
import { ApiEndpoints } from '../constantes/ApiEndpoints';
import { getSchemaIdFromStorage } from '../../../utils/getSchemaIdFromStorage';
import { generateDeviceData } from './AccountService';

export interface RequestOtpCodeResponse {
  otpId: string;
}

export interface ValidateOtpCodeResponse {
  validCode: boolean;
  accessToken?: string;
}

export interface RequestOtpCodeRequest {
  participantId: string;
  type: 'Sms' | 'Email';
  phoneNumber?: string;
  email?: string;
}

// Solicitar código OTP para signup
export const usePostSignupOtp = () => useMutation(
  async (data: RequestOtpCodeRequest) => {
    const schemaId = getSchemaIdFromStorage();
    data.phoneNumber = ValidatorField.extractNumbers(data?.phoneNumber || '');
    const response = await apiUserAccount.post(`${ApiEndpoints.auth.requestOtpCode}?schemaId=${schemaId}`, {
      ...data
    }) as any;
    
    // Retornando tanto a resposta quanto os dados da requisição
    return {
      result: response.data || response,
      requestData: data
    };
  },
  {
    onSuccess: ({result, requestData}) => {
      ApiLocalStorage.set('otp', {...result, ...requestData});
      
      notification.success({
        message: 'Código enviado',
        description: 'O código de validação foi enviado com sucesso.'
      });
    },
    onError: (error) => handleApiError(error, 'Erro ao enviar código para cadastro')
  }
);

// Validar código OTP
export const usePostValidateOtp = () => useMutation(
  async (code: string) => {
    const schemaId = getSchemaIdFromStorage();
    const otp = ApiLocalStorage.get('otp');
    
    if (!otp?.otpId) throw new Error('OTP ID não encontrado');
    const deviceData = await generateDeviceData();

    const response = await apiUserAccount.post(`${ApiEndpoints.auth.validateOtpCode}?schemaId=${schemaId}`, {
      code,
      otpId: otp.otpId,
      deviceData
    }) as any;
    
    return response.data || response;
  },
  {
    onSuccess: () => {
      notification.success({
        message: 'Código validado',
        description: 'O código foi validado com sucesso.'
      });
    },
    onError: (error) => handleApiError(error, 'Erro na validação')
  }
);

// Solicitar código OTP para senha
export const usePostPasswordOtp = () => useMutation(
  async (data: RequestOtpCodeRequest) => {
    const schemaId = getSchemaIdFromStorage();
    data.phoneNumber = ValidatorField.extractNumbers(data.phoneNumber || '');
    const response = await apiUserAccount.post(`${ApiEndpoints.auth.requestOtpCode}?schemaId=${schemaId}`, {
      ...data
    }) as any;
    
    // Retornando tanto a resposta quanto os dados da requisição
    return {
      result: response.data || response,
      requestData: data
    };
  },
  {
    onSuccess: ({result, requestData}) => {
      ApiLocalStorage.set('otp', {...result, ...requestData});
      
      notification.success({
        message: 'Código enviado',
        description: 'O código de validação foi enviado com sucesso.'
      });
    },
    onError: (error) => handleApiError(error, 'Erro ao enviar código')
  }
);

// Validar código OTP para senha
export const usePostValidatePasswordOtp = () => useMutation(
  async (code: string) => {
    const schemaId = getSchemaIdFromStorage();
    const otp = ApiLocalStorage.get('otp');
    
    if (!otp?.otpId) throw new Error('OTP ID não encontrado');
    const deviceData = await generateDeviceData();

    const response = await apiUserAccount.post(`${ApiEndpoints.auth.validateOtpCode}?schemaId=${schemaId}`, {
      code,
      otpId: otp.otpId,
      deviceData
    }) as any;
    
    return response.data || response;
  },
  {
    onSuccess: () => {
      notification.success({
        message: 'Código validado',
        description: 'O código foi validado com sucesso.'
      });
    },
    onError: (error) => handleApiError(error, 'Erro na validação')
  }
);

// Solicitar código 2FA
export const usePost2FARequest = () => useMutation(
  async (data: RequestOtpCodeRequest) => {
    data.phoneNumber = ValidatorField.extractNumbers(data.phoneNumber || '');
    const response = await apiUserAccount.post('/auth/otp/request', {
      ...data
    }) as any;
    
    // Retornando tanto a resposta quanto os dados da requisição
    return {
      result: response.data || response,
      requestData: data
    };
  },
  {
    onSuccess: ({result, requestData}) => {
      ApiLocalStorage.set('otp', {...result, ...requestData});
      
      notification.success({
        message: 'Código enviado',
        description: 'O código de autenticação foi enviado com sucesso.'
      });
    },
    onError: (error) => handleApiError(error, 'Erro ao enviar código')
  }
);

// Validar código 2FA
export const usePost2FAValidate = () => useMutation(
  async (code: string) => {
    const otp = ApiLocalStorage.get('otp');
    
    if (!otp?.otpId) throw new Error('OTP ID não encontrado');
    const deviceData = await generateDeviceData();

    const response = await apiUserAccount.post('/auth/otp/login', {
      code,
      otpId: otp.otpId,
      deviceData
    }) as any;
    
    return response.data || response;
  },
  {
    onSuccess: () => {
      notification.success({
        message: 'Autenticação concluída',
        description: 'O código foi validado com sucesso.'
      });
    },
    onError: (error) => handleApiError(error, 'Erro na validação')
  }
);
